import type {
    BackendModule,
    InitOptions,
    ReadCallback,
    ResourceKey,
    ResourceStore,
    Services,
} from 'i18next';
import { getTranslationGroup } from './translationGroups';

export type TranslationModuleLoader = () => Promise<string>;
export type TranslationModules = Record<string, TranslationModuleLoader>;

interface TranslationModule {
    language: string;
    namespace: string;
    load: TranslationModuleLoader;
}

interface WritableResourceStore extends ResourceStore {
    addResourceBundle(
        language: string,
        namespace: string,
        resource: ResourceKey,
        deep?: boolean,
        overwrite?: boolean,
        options?: { silent?: boolean },
    ): void;
}

const TRANSLATION_MODULE_PATH = /^\.\/translations\/([^/]+?)(?:\/(.+))?\.json$/;

export const getTranslationModulePath = (language: string, namespace: string): string =>
    namespace === 'translation'
        ? `./translations/${language}.json`
        : `./translations/${language}/${namespace.replaceAll('.', '/')}.json`;

const parseTranslationModule = (
    modulePath: string,
    load: TranslationModuleLoader,
): TranslationModule | null => {
    const match = TRANSLATION_MODULE_PATH.exec(modulePath);
    if (!match) {
        return null;
    }

    return {
        language: match[1],
        namespace: match[2]?.replaceAll('/', '.') ?? 'translation',
        load,
    };
};

const getGroupCacheKey = (language: string, namespace: string) =>
    `${language}:${getTranslationGroup(namespace)}`;

export const createTranslationBackend = (translationModules: TranslationModules): BackendModule => {
    const modulesByGroup = new Map<string, TranslationModule[]>();
    const groupResourcesCache = new Map<string, Promise<Record<string, ResourceKey>>>();
    let resourceStore: WritableResourceStore;

    for (const [modulePath, load] of Object.entries(translationModules)) {
        const translationModule = parseTranslationModule(modulePath, load);
        if (!translationModule) {
            continue;
        }

        const cacheKey = getGroupCacheKey(translationModule.language, translationModule.namespace);
        const modules = modulesByGroup.get(cacheKey) ?? [];
        modules.push(translationModule);
        modulesByGroup.set(cacheKey, modules);
    }

    const loadGroup = (language: string, namespace: string) => {
        const cacheKey = getGroupCacheKey(language, namespace);
        const cachedResources = groupResourcesCache.get(cacheKey);
        if (cachedResources) {
            return cachedResources;
        }

        const pendingResources = Promise.all(
            (modulesByGroup.get(cacheKey) ?? []).map(
                async (translationModule) =>
                    [
                        translationModule.namespace,
                        JSON.parse(await translationModule.load()) as ResourceKey,
                    ] as const,
            ),
        ).then((entries) => {
            const resources = Object.fromEntries(entries);
            for (const [loadedNamespace, resource] of entries) {
                resourceStore.addResourceBundle(language, loadedNamespace, resource, true, true, {
                    silent: true,
                });
            }
            return resources;
        });

        groupResourcesCache.set(cacheKey, pendingResources);
        return pendingResources;
    };

    return {
        type: 'backend',
        init(services: Services, _backendOptions: object, _i18nextOptions: InitOptions) {
            resourceStore = services.resourceStore as WritableResourceStore;
        },
        read(language: string, namespace: string, callback: ReadCallback) {
            void loadGroup(language, namespace)
                .then((resources) => callback(null, resources[namespace] ?? {}))
                .catch((error: unknown) =>
                    callback(error instanceof Error ? error : new Error(String(error)), false),
                );
        },
    };
};
